import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { financeApi } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import type { Group, TransactionPage } from "../../lib/types";
import type { StatisticsFilters, StatisticsView } from "./types";

export function useMonthlyOperationsQuery({
  group,
  filters,
  view,
  selectedParentCategory,
  selectedChildCategories,
  otherCategoryActive,
  otherCategoryIds,
}: {
  group: Group;
  filters: StatisticsFilters;
  view: StatisticsView;
  selectedParentCategory: string;
  selectedChildCategories: string[];
  otherCategoryActive: boolean;
  otherCategoryIds: string[];
}) {
  const operationFilters = useMemo(() => ({
    type: filters.type === "correction" ? "" as const : filters.type,
    userId: filters.userId || undefined,
    accountId: filters.accountId || undefined,
    tagIds: filters.tagIds,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    search: filters.merchant || undefined,
    limit: 12,
    offset: 0,
  }), [filters.accountId, filters.dateFrom, filters.dateTo, filters.merchant, filters.tagIds, filters.type, filters.userId]);
  const effectiveCategoryIds = selectedChildCategories.length
    ? selectedChildCategories
    : otherCategoryActive
      ? otherCategoryIds
      : selectedParentCategory
        ? [selectedParentCategory]
        : [];

  const query = useQuery({
    queryKey: queryKeys.statisticsOperations(group.id, {
      ...operationFilters,
      categoryIds: effectiveCategoryIds,
      otherActive: otherCategoryActive,
    }),
    queryFn: async (): Promise<TransactionPage> => {
      if (otherCategoryActive && !effectiveCategoryIds.length) {
        return { items: [], limit: operationFilters.limit, offset: operationFilters.offset, has_more: false, next_offset: null };
      }
      const requestFilters = {
        ...operationFilters,
        limit: operationFilters.type === "expense" || operationFilters.type === "income" ? 100 : 24,
      };
      let pages: TransactionPage[];
      if (effectiveCategoryIds.length <= 1) {
        const category = effectiveCategoryIds[0];
        pages = [await financeApi.transactionsPage(group.id, {
          ...requestFilters,
          categoryId: category && category !== "uncategorized" ? category : undefined,
          uncategorized: category === "uncategorized" || undefined,
        })];
      } else {
        pages = await Promise.all(effectiveCategoryIds.map((categoryId) => financeApi.transactionsPage(group.id, {
          ...requestFilters,
          categoryId: categoryId !== "uncategorized" ? categoryId : undefined,
          uncategorized: categoryId === "uncategorized" || undefined,
        })));
      }
      if (operationFilters.type === "transfer") {
        pages.push(await financeApi.transactionsPage(group.id, {
          ...requestFilters,
          type: "",
          linked: true,
          categoryId: undefined,
          uncategorized: undefined,
        }));
      }
      const merged = [...new Map(
        pages.flatMap((page) => page.items).map((transaction) => [transaction.id, transaction]),
      ).values()].sort((left, right) => right.occurred_at.localeCompare(left.occurred_at));
      const matching = merged.filter((transaction) => {
        if (operationFilters.type === "expense" || operationFilters.type === "income") {
          return transaction.type === operationFilters.type && !transaction.linked_transaction_id;
        }
        if (operationFilters.type === "transfer") {
          return transaction.type === "transfer" || Boolean(transaction.linked_transaction_id);
        }
        return true;
      });
      const seenLinkedPairs = new Set<string>();
      const logicalItems = matching.filter((transaction) => {
        if (!transaction.linked_transaction_id) return true;
        const pairKey = [transaction.id, transaction.linked_transaction_id].sort().join(":");
        if (seenLinkedPairs.has(pairKey)) return false;
        seenLinkedPairs.add(pairKey);
        return true;
      });
      return {
        ...pages[0],
        limit: operationFilters.limit,
        items: logicalItems.slice(0, operationFilters.limit),
        has_more: pages.some((page) => page.has_more) || logicalItems.length > operationFilters.limit,
      };
    },
    enabled: view === "overview",
    placeholderData: (previous) => previous,
  });

  return { operationsQuery: query, effectiveCategoryIds };
}
